import {
  getErrorMessage,
  getErrorTitle,
  shouldContactSupport,
  SOPORTE_TECNICO,
} from "@/services/utils/httpUtils";

/**
 * Plugin de notificaciones para Vue 3
 * Permite usar this.$notify en cualquier componente
 */
export default {
  install(app, options = {}) {
    const notify = {
      /**
       * Notificación de éxito
       * @example this.$notify.success('Despacho creado correctamente')
       */
      success(message, title = "Éxito", duration = 4000) {
        app.config.globalProperties.$store.dispatch(
          "notificationStore/success",
          {
            message,
            title,
            duration,
          }
        );
      },

      /**
       * Notificación de error
       * @example this.$notify.error('No se pudo eliminar el despacho')
       */
      error(message, title = "Error", duration = 6000, options = {}) {
        const isServerOrSystem =
          title.toLowerCase().includes("servidor") ||
          title.toLowerCase().includes("sistema") ||
          message.toLowerCase().includes("soporte");

        app.config.globalProperties.$store.dispatch("notificationStore/error", {
          message,
          title,
          duration: isServerOrSystem ? 10000 : duration,
          contactSupport:
            options.contactSupport !== undefined
              ? options.contactSupport
              : isServerOrSystem,
          errorDetails: options.errorDetails || null,
        });
      },

      /**
       * Notificación de advertencia
       * @example this.$notify.warning('Este campo es obligatorio')
       */
      warning(message, title = "Advertencia", duration = 5000) {
        app.config.globalProperties.$store.dispatch(
          "notificationStore/warning",
          {
            message,
            title,
            duration,
          }
        );
      },

      /**
       * Notificación informativa
       * @example this.$notify.info('Se están cargando los datos')
       */
      info(message, title = "Información", duration = 4000) {
        app.config.globalProperties.$store.dispatch("notificationStore/info", {
          message,
          title,
          duration,
        });
      },

      /**
       * Maneja errores de API automáticamente
       * Extrae el mensaje del error y lo muestra como notificación
       * Clasifica correctamente errores de servidor/código como errores propios
       * @param {Error} error - El objeto de error (de Axios o genérico)
       * @param {string} defaultMessage - Mensaje por defecto si no se puede extraer uno
       * @example this.$notify.apiError(error, 'Error al crear el despacho')
       */
      apiError(error, defaultMessage = "Ha ocurrido un error") {
        const message = getErrorMessage(error, defaultMessage);
        const title = getErrorTitle(error);
        const contactSupport = shouldContactSupport(error);

        // Log para desarrollo y diagnóstico
        console.error(`[API Error] ${defaultMessage}:`, error);

        app.config.globalProperties.$store.dispatch("notificationStore/error", {
          message,
          title,
          duration: contactSupport ? 10000 : 8000,
          contactSupport,
          errorDetails: error?.message || null,
        });
      },

      /**
       * Maneja errores de validación del frontend
       * @param {string} message - Mensaje de validación
       * @example this.$notify.validation('El campo nombre es obligatorio')
       */
      validation(message, title = "Validación") {
        app.config.globalProperties.$store.dispatch(
          "notificationStore/warning",
          {
            message,
            title,
            duration: 5000,
          }
        );
      },

      /**
       * Maneja errores de red o comunicación
       * NUNCA asume falta de internet si el cliente está online.
       * @param {Error} error - El objeto de error
       * @example this.$notify.networkError(error)
       */
      networkError(error) {
        const message = getErrorMessage(error);
        const title = getErrorTitle(error);
        const contactSupport = shouldContactSupport(error);

        console.error("[Network/System Error]:", error);

        app.config.globalProperties.$store.dispatch("notificationStore/error", {
          message,
          title,
          duration: contactSupport ? 10000 : 8000,
          contactSupport,
          errorDetails: error?.message || null,
        });
      },
    };

    // Agregar $notify a la instancia global
    app.config.globalProperties.$notify = notify;

    // También disponible como provide/inject
    app.provide("notify", notify);
  },
};
